import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { getUnit, getCurrentUnit, setCurrentUnit as setCurrentUnitDB } from '@/lib/services/units'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Role } from '@/lib/types'

interface Unit {
  id: string
  name: string
  slug: string
  context_id?: string | null
  context_key?: string
  [key: string]: any
}

interface UnitContextType {
  currentUnit: Unit | null
  contextKey: string | null
  userRole: Role | null
  isLoading: boolean
  setCurrentUnit: (unitId: string) => Promise<void>
  refreshCurrentUnit: () => Promise<void>
  updateGlobalContext: (newContextKey: string) => void
}

const UnitContext = createContext<UnitContextType | undefined>(undefined)

export function UnitProvider({ children }: { children: ReactNode }) {
  const [currentUnit, setCurrentUnitState] = useState<Unit | null>(null)
  const [contextKey, setContextKey] = useState<string | null>(null)
  const [userRole, setUserRole] = useState<Role | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { user, loading: authLoading } = useAuth()

  // Load current unit when authenticated user becomes available
  useEffect(() => {
    if (user?.id) {
      loadCurrentUnit()
    }
  }, [user?.id])

  // When auth finishes loading with no user, there is nothing to load — mark as done
  useEffect(() => {
    if (!authLoading && !user?.id) {
      setIsLoading(false)
    }
  }, [authLoading, user?.id])

  // When connection is restored, refresh current unit to ensure context is up-to-date
  useEffect(() => {
    const handleOnline = () => {
      console.log('[UnitContext] online event - refreshing current unit')
      if (user?.id) {
        loadCurrentUnit().catch(err => console.error('[UnitContext] error refreshing unit on online', err))
      }
    }

    window.addEventListener('online', handleOnline)
    return () => window.removeEventListener('online', handleOnline)
  }, [user?.id])
  
  // Log when contextKey changes for debugging
  useEffect(() => {
    console.log('[UnitContext] contextKey changed to:', contextKey)
  }, [contextKey])

  const loadCurrentUnit = async () => {
    const userId = user?.id
    if (!userId) return

    try {
      setIsLoading(true)

      console.log('[UnitContext] Loading current unit for user:', userId)

      // Get current unit from database
      const unit = await getCurrentUnit(userId)
      
      if (unit) {
        console.log('[UnitContext] Current unit found:', unit.id, 'context_id:', unit.context_id)
        
        // Enrich with context_key
        const enrichedUnit = await getUnit(unit.id)
        
        console.log('[UnitContext] Enriched unit context_key:', enrichedUnit.context_key)
        
        setCurrentUnitState(enrichedUnit as Unit)
        
        // Resolve user role in this unit
        const { data: membership } = await supabase
          .from('unit_members')
          .select('role')
          .eq('unit_id', unit.id)
          .eq('user_id', userId)
          .maybeSingle()
        const resolvedRole: Role = (membership?.role as Role) ?? ((enrichedUnit as any).created_by === userId ? 'admin' : 'viewer')
        setUserRole(resolvedRole)
        
        // Update contextKey state - this should trigger re-render in TranslationProvider
        const newContextKey = enrichedUnit.context_key || null
        console.log('[UnitContext] Setting contextKey from:', contextKey, 'to:', newContextKey)
        setContextKey(newContextKey)
      } else {
        console.log('[UnitContext] No current unit found')
        setCurrentUnitState(null)
        setContextKey(null)
        setUserRole(null)
      }
    } catch (error) {
      console.error('[UnitContext] Error loading current unit:', error)
      setCurrentUnitState(null)
      setContextKey(null)
      setUserRole(null)
    } finally {
      setIsLoading(false)
    }
  }

  const setCurrentUnit = async (unitId: string) => {
    const userId = user?.id
    if (!userId) {
      console.warn('[UnitContext] Cannot set current unit: no userId')
      return
    }

    try {
      console.log('[UnitContext] Setting current unit:', unitId, 'for user:', userId)
      setIsLoading(true)
      
      // Update in database
      await setCurrentUnitDB(userId, unitId)
      console.log('[UnitContext] Database updated successfully')
      
      // Reload current unit
      await loadCurrentUnit()
      console.log('[UnitContext] Current unit reloaded')
    } catch (error) {
      console.error('[UnitContext] Error setting current unit:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const refreshCurrentUnit = async () => {
    await loadCurrentUnit()
  }

  const updateGlobalContext = (newContextKey: string) => {
    console.log(`[UnitContext] Updating global context to: ${newContextKey}`)
    setContextKey(newContextKey)
  }

  return (
    <UnitContext.Provider
      value={{
        currentUnit,
        contextKey,
        userRole,
        isLoading,
        setCurrentUnit,
        refreshCurrentUnit,
        updateGlobalContext
      }}
    >
      {children}
    </UnitContext.Provider>
  )
}

export function useUnit() {
  const context = useContext(UnitContext)
  if (context === undefined) {
    throw new Error('useUnit must be used within a UnitProvider')
  }
  return context
}

// Hook to get context key with fallback to env variable
export function useContextKey(): string {
  const { contextKey, currentUnit } = useUnit()

  // Se há um contexto fixo definido no .env, ele é a prioridade máxima.
  // Isso garante que apps com VITE_PUBLIC_APP_CONTEXT definido não sejam
  // sobrescritos pelo context_key da unidade no banco de dados.
  const fixedEnvContext = import.meta.env.VITE_PUBLIC_APP_CONTEXT
  if (fixedEnvContext) return fixedEnvContext

  // Prioridades quando não há contexto fixo:
  // 1. Contexto da unidade atual
  // 2. Contexto do hook
  // 3. Fallback padrão
  return currentUnit?.context_key || 
         contextKey || 
         'farm'
}
